import { create } from "zustand";
import { persist } from "zustand/middleware";
import { ReportData } from "~/types/global";

interface ReportListStoreActions {
  setReportList: (list: ReportData[]) => void;
  incrementReportList: (list: ReportData[]) => void;
  appendReportList: (list: ReportData[]) => void;
  updateItemById: (reportId: string, fields: Partial<ReportData>) => void;
  setPage: (page: number) => void;
  incrementPage: () => void;
  setOffset: (offset: number) => void;
  incrementOffeset: () => void;
  setLoadCount: (count: number) => void;
  setSearchFilter: (filter: string) => void;
  clearSearchFilter: (clearInput?: boolean) => void;
  resetPagination: () => void;
  setLoadMore: (loadMore: boolean) => void;
  addFolderToPath: (folder: { id: string; name: string }) => void;
  removeFolderFromPath: (folderId: string) => void;
  removeFoldersAfterIndex: (index: number) => void;
  clearFolderPath: () => void;
  resetShouldClearInput: () => void;
  setLayout: (layout: "masonry" | "column") => void;
}

interface FolderPathItem {
  id: string;
  name: string;
}

interface ReportListStoreState {
  list: ReportData[];
  searchFilter: string;
  shouldClearInput: boolean;
  loadCount: number;
  page: number;
  offset: number;
  loadMore: boolean;
  folderPath: FolderPathItem[];
  layout: "masonry" | "column";
  actions: ReportListStoreActions;
}

const useReportListStore = create<ReportListStoreState>()(
  persist(
    (set) => ({
      list: [],
      searchFilter: "",
      shouldClearInput: false,
      loadCount: 25,
      page: 1,
      offset: 0,
      loadMore: false,
      folderPath: [],
      layout: "masonry",
      actions: {
        // TODO - implementar paginação - front manter o estado inicial da lista e adiciona os resultados subsequentes da API
        setReportList: (list: ReportData[]) => set({ list }),
        incrementReportList: (list: ReportData[]) =>
          set((state) => ({ list: [...state.list, ...list] })),
        appendReportList: (newItems: ReportData[]) =>
          set((state) => ({ list: [...state.list, ...newItems] })),
        updateItemById: (reportId: string, fields: Partial<ReportData>) =>
          set((state) => ({
            list: state.list.map((item) =>
              item.user_reports_id === reportId ? { ...item, ...fields } : item
            ),
          })),
        setPage: (page: number) => set({ page }),
        setOffset: (offset: number) => set({ offset }),
        incrementOffeset: () => set((state) => ({ offset: state.offset + state.loadCount })),
        incrementPage: () => set((state) => ({ page: state.page + 1 })),
        setLoadCount: (count: number) => set((state) => ({ loadCount: state.loadCount + count })),
        setSearchFilter: (filter: string) => set({ searchFilter: filter }),
        clearSearchFilter: (clearInput = false) =>
          set({ searchFilter: "", shouldClearInput: clearInput }),
        resetPagination: async () => set({ page: 1, loadCount: 25 }),
        setLoadMore: (loadMore: boolean) => set({ loadMore }),
        addFolderToPath: (folder: { id: string; name: string }) =>
          set((state) => {
            const newPath = [...state.folderPath, folder];
            sessionStorage.setItem("folder-path-storage", JSON.stringify(newPath));
            return { folderPath: newPath };
          }),
        removeFolderFromPath: (folderId: string) =>
          set((state) => {
            const newPath = state.folderPath.filter((folder) => folder.id !== folderId);
            sessionStorage.setItem("folder-path-storage", JSON.stringify(newPath));
            return { folderPath: newPath };
          }),
        removeFoldersAfterIndex: (index: number) =>
          set((state) => {
            const newPath = state.folderPath.slice(0, index + 1);
            sessionStorage.setItem("folder-path-storage", JSON.stringify(newPath));
            return { folderPath: newPath };
          }),
        clearFolderPath: () => {
          sessionStorage.removeItem("folder-path-storage");
          return set({ folderPath: [] });
        },
        resetShouldClearInput: () => set({ shouldClearInput: false }),
        setLayout: (layout: "masonry" | "column") => set({ layout }),
      },
    }),
    {
      name: "report-list-storage",
      partialize: (state) => ({
        layout: state.layout,
      }),
    }
  )
);

// Hydrate folderPath from sessionStorage on load
const savedFolderPath = sessionStorage.getItem("folder-path-storage");
if (savedFolderPath) {
  try {
    const parsed = JSON.parse(savedFolderPath);
    if (Array.isArray(parsed)) {
      useReportListStore.setState({ folderPath: parsed });
    }
  } catch {}
}

export const useReportList = () => useReportListStore((state) => state.list);
export const useReportListSearchFilter = () => useReportListStore((state) => state.searchFilter);
export const useReportListShouldClearInput = () =>
  useReportListStore((state) => state.shouldClearInput);
export const useReportListLoadCount = () => useReportListStore((state) => state.loadCount);
export const useReportListPage = () => useReportListStore((state) => state.page);
export const useReportListLoadMore = () => useReportListStore((state) => state.loadMore);
export const useReportListOffset = () => useReportListStore((state) => state.offset);
export const useFolderPath = () => useReportListStore((state) => state.folderPath);
export const useReportListLayout = () => useReportListStore((state) => state.layout);
export const useReportListActions = () => useReportListStore((state) => state.actions);
